import {assert} from 'assert-ts';
import {query} from '.';
import {compare} from './functions';
import {Filter, IEnumerable, IQueryable, KeySelector, TGroup} from './types';

const getValue = <T, TValue = any>(item: T, key: KeySelector<T, TValue>) =>
  typeof key === 'function' ? key(item) : item[key];

export class Query<T> implements IQueryable<T> {
  private _list: T[];

  constructor(list: T[]) {
    this._list = list;
  }

  toArray(): T[] {
    return this._list;
  }

  where(filter: Filter<T>): IQueryable<T> {
    return typeof filter === 'function'
      ? new Query(this._list.filter(filter))
      : new Query(
          this._list.filter((item: T) =>
            Object.keys(filter).reduce<boolean>(
              (acc, key) =>
                acc && (item as never)[key] === (filter as never)[key],
              true,
            ),
          ),
        );
  }

  join<T2, TJoin>(
    enumerable: IEnumerable<T2>,
    key1: KeySelector<T>,
    key2: KeySelector<T2>,
    select: (v1: T, v2: T2) => TJoin,
  ): IQueryable<TJoin> {
    const t2List: T2[] = Array.isArray(enumerable)
      ? enumerable
      : enumerable.toArray();

    return new Query(
      t2List
        .map(t2 => ({
          t2,
          t1: this._list.find(t1 => getValue(t1, key1) === getValue(t2, key2)),
        }))
        .filter(j => j.t1 && j.t2)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map(j => select(j.t1!, j.t2!)),
    );
  }

  select<TSelect>(
    select: (t1: T, index: number) => TSelect,
  ): IQueryable<TSelect> {
    return new Query(this._list.map(select));
  }

  groupBy<TSelect extends any = T[]>(
    key: KeySelector<T>,
    select?: (t1: T[]) => TSelect,
  ): TGroup<TSelect> {
    const grouped = this._list.reduce<TGroup<T[]>>((acc, t) => {
      const keyVal = getValue(t, key);
      const groupKey: string | number =
        typeof keyVal === 'string' || typeof keyVal === 'number'
          ? keyVal
          : keyVal === undefined
          ? 'undefined'
          : JSON.stringify(keyVal);
      const group = acc[groupKey] || [];
      group.push(t);
      acc[groupKey] = group;
      return acc;
    }, {});

    if (!select) {
      return (grouped as unknown) as TGroup<TSelect>;
    }

    const groupWithSelect: TGroup<TSelect> = {};
    Object.keys(grouped).forEach(key => {
      groupWithSelect[key] = select(grouped[key]);
    });

    return groupWithSelect;
  }

  groupByJoin<T2, TJoin>(
    list2: T2[],
    key1: KeySelector<T>,
    key2: KeySelector<T2>,
    select: (i1: T | undefined, sublist2: T2[]) => TJoin,
  ): IQueryable<TJoin> {
    const group1 = this.groupBy(key1);
    const group2 = query(list2).groupBy(key2);
    const result: TJoin[] = [];

    Object.keys(group1).forEach(key1Val => {
      assert(
        group1[key1Val].length === 1,
        'query.outerGroupByJoin requires unique left key (key1) values',
        () => ({key: key1, keyVal: key1Val, items: group1[key1Val]}),
      );
      result.push(select(group1[key1Val][0], group2[key1Val] || []));
    });

    return query(result);
  }

  outerGroupByJoin<T2, TJoin>(
    list2: T2[],
    key1: KeySelector<T>,
    key2: KeySelector<T2>,
    select: (i1: T | undefined, sublist2: T2[]) => TJoin,
  ): IQueryable<TJoin> {
    const group1 = this.groupBy(key1);
    const group2 = query(list2).groupBy(key2);
    const result: TJoin[] = [];

    Object.keys(group1).forEach(key1Val => {
      assert(
        group1[key1Val].length === 1,
        'query.outerGroupByJoin requires unique left key (key1) values',
        () => ({key: key1, keyVal: key1Val, items: group1[key1Val]}),
      );
      result.push(select(group1[key1Val][0], group2[key1Val] || []));
    });

    // Also push any items in group2 not having a matching key1
    // - collect
    const restItems2: T2[] = [];
    Object.keys(group2).forEach(key2Val => {
      if (!group1[key2Val]) {
        restItems2.push(...group2[key2Val]);
      }
    });
    // - add if any
    if (restItems2.length > 0) {
      result.push(select(undefined, restItems2));
    }

    return query(result);
  }

  skip(n: number): IQueryable<T> {
    return new Query(this._list.slice(n));
  }

  take(n: number): IQueryable<T> {
    return new Query(this._list.slice(0, n));
  }

  orderBy(key: KeySelector<T>): IQueryable<T> {
    if (this._list.length === 0) {
      return this;
    }

    return new Query([...this._list].sort((a, b) => compare(key, a, b)));
  }

  orderByDescending(key: KeySelector<T>): IQueryable<T> {
    if (this._list.length === 0) {
      return this;
    }

    return new Query([...this._list].sort((a, b) => -compare(key, a, b)));
  }

  any(condition: Filter<T>): boolean {
    return this.first(condition) !== undefined;
  }

  first(filter?: Filter<T>): T | undefined {
    return filter ? this.where(filter).first() : this._list[0];
  }

  last(filter?: Filter<T>): T | undefined {
    return filter
      ? this.where(filter).last()
      : this._list.length > 0
      ? this._list[this._list.length - 1]
      : undefined;
  }

  singleOrUndefined(filter: Filter<T>): T | undefined {
    const matches = this.where(filter);
    assert(
      matches.count() <= 1,
      'query.singleOrUndefined: more items match filter',
      {matches: matches.toArray()},
    );
    return matches.first();
  }

  count(): number {
    return this._list.length;
  }

  private aggregateNumber(
    key: KeySelector<T>,
    aggregate: (acc: number, val: number) => number,
    seed = 0,
  ): number {
    return this._list.reduce((acc, t) => {
      const keyVal = getValue(t, key);
      return aggregate(acc, typeof keyVal === 'number' ? keyVal : seed);
    }, seed);
  }

  sum(key: KeySelector<T>): number {
    return this.aggregateNumber(key, (a, b) => a + b);
  }

  min(key: KeySelector<T>): number {
    return Math.min(...this.select(t => getValue(t, key)).toArray());
  }

  max(key: KeySelector<T>): number {
    return Math.max(...this.select(t => getValue(t, key)).toArray());
  }
}
